class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 0);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.onFacetFormInput.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    FacetFiltersForm.syncInlineChipVisualState(this, { fromUrl: true });
  }

  /**
   * Puces Tag de secours : aligne les checkboxes sur filter.p.tag dans l’URL.
   */
  static syncTagFallbackCheckedFromUrl(root = document) {
    const chips = root.querySelectorAll('[data-tag-fallback-chip]');
    if (!chips.length) return false;

    const params = new URLSearchParams(window.location.search);
    const activeTags = new Set(
      params
        .getAll('filter.p.tag')
        .map((tag) => String(tag).trim().toLowerCase())
        .filter(Boolean)
    );

    chips.forEach((input) => {
      input.checked = activeTags.has(String(input.value || '').trim().toLowerCase());
    });
    return true;
  }

  /**
   * Classes button--tertiary / primary + état « Voir tout » depuis les checkboxes.
   * @param {ParentNode} root
   * @param {{ fromUrl?: boolean }} [options] fromUrl = resync des puces fallback depuis l’URL d’abord
   */
  static syncInlineChipVisualState(root = document, options = {}) {
    const form =
      (root.querySelector && root.querySelector('#FacetFiltersForm')) ||
      (root.id === 'FacetFiltersForm' ? root : null) ||
      document.getElementById('FacetFiltersForm');
    const scope = form || root;

    if (options.fromUrl) {
      FacetFiltersForm.syncTagFallbackCheckedFromUrl(scope);
    }

    let anyChipActive = false;
    scope.querySelectorAll('label.facets-inline-chip:not(.facets-inline-see-all)').forEach((label) => {
      const input = label.querySelector(
        'input.facets-inline-chip__input, input.swatch-input__input, input[type="checkbox"]'
      );
      if (!input) return;
      const active = Boolean(input.checked);
      if (active) anyChipActive = true;
      label.classList.toggle('button--tertiary', active);
      label.classList.toggle('button--primary', !active);
    });

    const seeAllInput = scope.querySelector('.facets-inline-see-all__input');
    const seeAllLabel = seeAllInput?.closest('label.facets-inline-see-all');
    if (seeAllInput && seeAllLabel) {
      seeAllInput.checked = !anyChipActive;
      seeAllLabel.classList.toggle('button--tertiary', !anyChipActive);
      seeAllLabel.classList.toggle('button--primary', anyChipActive);
      if (anyChipActive) {
        seeAllLabel.dataset.viewAllDefault = '';
      }
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
      FacetFiltersForm.syncInlineChipVisualState(document, { fromUrl: true });
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.reinitializeProductGridAfterSwap();
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.reinitializeProductGridAfterSwap();
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  /** Après swap HTML de la grille (facettes) : scroll-trigger + titres banner-stagger-reveal. */
  static reinitializeProductGridAfterSwap() {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(gridContainer);
    }
    if (typeof window.initBannerStaggerReveal === 'function') {
      window.initBannerStaggerReveal(gridContainer);
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsed.getElementById('ProductCount');
    const count = countEl ? countEl.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      if (!jsFilter) return false;
      // Puces "Voir tout" : pas de .facets-wrap, renderCounts ne met rien à jour — il faut le innerHTML du fetch.
      if (jsFilter.classList.contains('facets-inline-group--see-all')) return false;
      return element.id === jsFilter.id;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    // Après renderCounts : le HTML serveur (fallback = toujours primary) écrase les classes.
    FacetFiltersForm.syncInlineChipVisualState(document, { fromUrl: true });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onFacetFormInput(event) {
    if (event.target.classList && event.target.classList.contains('facets-inline-see-all__input')) {
      this.onViewAllInputImmediate(event);
      return;
    }
    const form = event.target.closest('form');
    if (form) {
      this.enforceSingleInlineFacetSelection(form, event.target);
      if (form.classList.contains('facets__form--inline-chips')) {
        // Feedback immédiat (avant fetch) ; pas fromUrl pour ne pas annuler le clic.
        FacetFiltersForm.syncInlineChipVisualState(form);
      }
    }
    this.debouncedOnSubmit(event);
  }

  /**
   * Collection puces inline : une seule facette (hors prix) active à la fois ; prix exclusif des puces.
   */
  enforceSingleInlineFacetSelection(form, target) {
    if (!form.classList.contains('facets__form--inline-chips')) return;

    const isChip =
      target.matches?.('.facets-inline-chip__input') ||
      (target.matches?.('.swatch-input__input') && target.closest('.facets-inline-chip'));

    const priceRangeEl = form.querySelector('price-range.facets__price--inline');
    const isPriceField =
      priceRangeEl &&
      priceRangeEl.contains(target) &&
      target.matches?.('input.field__input');

    if (isChip && target.checked) {
      form.querySelectorAll('.facets-inline-chip__input').forEach((el) => {
        if (el !== target) el.checked = false;
      });
      form.querySelectorAll('.facets-inline-chip .swatch-input__input').forEach((el) => {
        if (el !== target) el.checked = false;
      });
      if (priceRangeEl) {
        priceRangeEl.querySelectorAll('input.field__input').forEach((el) => {
          el.value = '';
        });
      }
      return;
    }

    if (isPriceField && String(target.value || '').trim() !== '') {
      form.querySelectorAll('.facets-inline-chip__input').forEach((el) => {
        el.checked = false;
      });
      form.querySelectorAll('.facets-inline-chip .swatch-input__input').forEach((el) => {
        el.checked = false;
      });
    }
  }

  onViewAllInputImmediate(event) {
    const input = event.target;
    const label = input?.closest('label.facets-inline-see-all');
    if (!label) return;

    const viewAllDefault = label.dataset.viewAllDefault === 'true';
    if (!input.checked && viewAllDefault) {
      input.checked = true;
      return;
    }

    let base = label.dataset.resultsUrl || '';
    const qIndex = base.indexOf('?');
    const url = qIndex === -1 ? '' : base.slice(qIndex + 1);
    FacetFiltersForm.renderPage(url, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
